import api from '@/shared/utils/apiConfig';
import type {
  CannedResponse,
  MessagingCapabilitiesResponse,
  MessagingConversation,
  MessagingMessage,
} from '../types';

export interface MessagingInboxPage {
  id: string;
  label: string;
  channel: string;
}

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
  message?: string;
  count?: number;
  total?: number;
  page?: number;
  pages?: number;
  counts?: {
    all: number;
    mine: number;
    unassigned: number;
    unread: number;
  };
}

export const messagingAPI = {
  getCapabilities: async (): Promise<ApiEnvelope<MessagingCapabilitiesResponse>> => {
    const response = await api.get('/messaging/capabilities');
    return response.data;
  },

  listConversations: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    unread?: boolean;
    assigned?: 'me' | 'unassigned';
    assignedUserId?: string;
    channel?: string;
    providerAccountId?: string;
    crm?: string;
    patientId?: string;
    leadId?: string;
    status?: 'open' | 'closed' | 'all';
    hasContact?: boolean;
    fromAd?: boolean;
    adId?: string;
    lastMessageFrom?: string;
    lastMessageTo?: string;
    leadStatus?: string;
  }): Promise<ApiEnvelope<MessagingConversation[]>> => {
    const response = await api.get('/messaging/conversations', {
      params: {
        page: params?.page,
        limit: params?.limit,
        search: params?.search || undefined,
        unread: params?.unread ? '1' : undefined,
        assigned: params?.assigned,
        assignedUserId: params?.assignedUserId || undefined,
        channel: params?.channel || undefined,
        providerAccountId: params?.providerAccountId || undefined,
        crm: params?.crm || undefined,
        patientId: params?.patientId || undefined,
        leadId: params?.leadId || undefined,
        status: params?.status,
        hasContact: params?.hasContact ? '1' : undefined,
        fromAd: params?.fromAd ? '1' : undefined,
        adId: params?.adId || undefined,
        lastMessageFrom: params?.lastMessageFrom || undefined,
        lastMessageTo: params?.lastMessageTo || undefined,
        leadStatus: params?.leadStatus || undefined,
      },
    });
    return response.data;
  },

  listPages: async (): Promise<ApiEnvelope<MessagingInboxPage[]>> => {
    const response = await api.get('/messaging/pages');
    return response.data;
  },

  getConversation: async (
    id: string
  ): Promise<ApiEnvelope<MessagingConversation>> => {
    const response = await api.get(`/messaging/conversations/${id}`);
    return response.data;
  },

  listMessages: async (
    conversationId: string,
    params?: { limit?: number; before?: string; beforeId?: string }
  ): Promise<ApiEnvelope<MessagingMessage[]>> => {
    const response = await api.get(
      `/messaging/conversations/${conversationId}/messages`,
      { params }
    );
    return response.data;
  },

  fetchHistory: async (
    conversationId: string,
    payload?: { direction?: 'older'; limit?: number }
  ): Promise<ApiEnvelope<{ jobId: string; status: 'queued' | 'running' }>> => {
    const response = await api.post(
      `/messaging/conversations/${conversationId}/fetch-history`,
      payload ?? { direction: 'older' },
      { timeout: 15_000 }
    );
    return response.data;
  },

  getHistorySync: async (
    conversationId: string,
    params?: { hasMoreLocal?: boolean }
  ): Promise<
    ApiEnvelope<{
      state: 'unknown' | 'available' | 'fetching' | 'exhausted' | 'error';
      hasMoreOlder: boolean | null;
      oldestSyncedAt: string | null;
      oldestProviderMessageId: string | null;
      lastFetchedAt: string | null;
      lastError: string | null;
      canLoadOlder: boolean;
      activeJob: { id: string; status: string } | null;
    }>
  > => {
    const response = await api.get(`/messaging/conversations/${conversationId}/history-sync`, {
      params: {
        hasMoreLocal: params?.hasMoreLocal ? '1' : undefined,
      },
    });
    return response.data;
  },

  getSyncJob: async (
    jobId: string
  ): Promise<
    ApiEnvelope<{
      id: string;
      status: string;
      type: string;
      lastError: string | null;
      finishedAt: string | null;
    }>
  > => {
    const response = await api.get(`/messaging/sync-jobs/${jobId}`);
    return response.data;
  },

  downloadAttachment: async (apiPath: string, disposition: 'inline' | 'attachment' = 'inline'): Promise<Blob> => {
    const path = apiPath.startsWith('/') ? apiPath : `/${apiPath}`;
    const response = await api.get(path, {
      params: { disposition },
      responseType: 'blob',
    });
    return response.data as Blob;
  },

  sendMessage: async (
    conversationId: string,
    payload: string | FormData
  ): Promise<ApiEnvelope<MessagingMessage>> => {
    const isForm = payload instanceof FormData;
    const response = await api.post(
      `/messaging/conversations/${conversationId}/messages`,
      isForm ? payload : { text: payload },
      isForm
        ? {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 120_000,
          }
        : undefined
    );
    return response.data;
  },

  retryMessage: async (
    conversationId: string,
    messageId: string
  ): Promise<ApiEnvelope<MessagingMessage>> => {
    const response = await api.post(
      `/messaging/conversations/${conversationId}/messages/${messageId}/retry`
    );
    return response.data;
  },

  retryAttachment: async (
    conversationId: string,
    messageId: string,
    attachmentId: string
  ): Promise<ApiEnvelope<MessagingMessage>> => {
    const response = await api.post(
      `/messaging/conversations/${conversationId}/messages/${messageId}/attachments/${attachmentId}/retry`
    );
    return response.data;
  },

  linkCustomer: async (
    conversationId: string,
    payload: { leadId?: string | null; patientId?: string | null }
  ): Promise<ApiEnvelope<MessagingConversation>> => {
    const response = await api.post(
      `/messaging/conversations/${conversationId}/link`,
      payload
    );
    return response.data;
  },

  createLead: async (
    conversationId: string,
    payload?: { name?: string; phone?: string; email?: string }
  ): Promise<ApiEnvelope<MessagingConversation>> => {
    const response = await api.post(
      `/messaging/conversations/${conversationId}/leads`,
      payload || {}
    );
    return response.data;
  },

  dismissContactSuggestion: async (
    conversationId: string
  ): Promise<ApiEnvelope<MessagingConversation>> => {
    const response = await api.post(
      `/messaging/conversations/${conversationId}/contact-suggestion/dismiss`
    );
    return response.data;
  },

  assignConversation: async (
    conversationId: string,
    assignedUserId: string | null
  ): Promise<ApiEnvelope<MessagingConversation>> => {
    const response = await api.post(
      `/messaging/conversations/${conversationId}/assign`,
      { assignedUserId }
    );
    return response.data;
  },

  markRead: async (
    conversationId: string
  ): Promise<ApiEnvelope<MessagingConversation>> => {
    const response = await api.post(
      `/messaging/conversations/${conversationId}/read`
    );
    return response.data;
  },

  postNote: async (
    conversationId: string,
    text: string
  ): Promise<ApiEnvelope<MessagingMessage>> => {
    const response = await api.post(
      `/messaging/conversations/${conversationId}/notes`,
      { text }
    );
    return response.data;
  },

  setStatus: async (
    conversationId: string,
    status: 'open' | 'closed'
  ): Promise<ApiEnvelope<MessagingConversation>> => {
    const response = await api.post(
      `/messaging/conversations/${conversationId}/status`,
      { status }
    );
    return response.data;
  },

  listAssignees: async (): Promise<
    ApiEnvelope<Array<{ id: string; name: string | null }>>
  > => {
    const response = await api.get('/messaging/assignees');
    return response.data;
  },

  listCannedResponses: async (params?: {
    includeInactive?: boolean;
  }): Promise<ApiEnvelope<CannedResponse[]>> => {
    const response = await api.get('/messaging/canned-responses', {
      params: {
        includeInactive: params?.includeInactive ? '1' : undefined,
      },
    });
    return response.data;
  },

  createCannedResponse: async (payload: {
    shortcode: string;
    title: string;
    content: string;
  }): Promise<ApiEnvelope<CannedResponse>> => {
    const response = await api.post('/messaging/canned-responses', payload);
    return response.data;
  },

  updateCannedResponse: async (
    id: string,
    payload: {
      shortcode?: string;
      title?: string;
      content?: string;
      isActive?: boolean;
    }
  ): Promise<ApiEnvelope<CannedResponse>> => {
    const response = await api.patch(`/messaging/canned-responses/${id}`, payload);
    return response.data;
  },

  deleteCannedResponse: async (id: string): Promise<ApiEnvelope<unknown>> => {
    const response = await api.delete(`/messaging/canned-responses/${id}`);
    return response.data;
  },

  sync: async (): Promise<
    ApiEnvelope<{
      jobs: Array<{
        id: string;
        pageId: string;
        type: string;
        status: string;
      }>;
    }>
  > => {
    const response = await api.post('/messaging/sync', undefined, { timeout: 15_000 });
    return response.data;
  },
};
